import { API } from "@swivl-mono/api-helper";
import { LocationVarKey, MergedLocationVar } from "@swivl-mono/types";
import { Button, ButtonVariant } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

import { useSavedIndicator } from "../../../Components/SavedIndicator/SavedIndicator";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import { TopicDraft, TopicProvider } from "../../../Views/Properties/CortexV3/Hooks/useTopicContext";
import { TOPIC_COMPONENTS } from "../../../Views/Properties/CortexV3/Topics";

interface CortexTopicWorksheetProps {
  /** Cortex catalog topic id (must be registered in `TOPIC_COMPONENTS`). */
  topicId: string;
  /** Section heading. Omit to render just the box with no title. */
  heading?: string;
  description?: string;
  /**
   * When true, edits persist automatically a short beat after the last change —
   * no manual Save button. Opt-in: only safe for structured-var topics (which
   * upsert directly via `saveRaw`). Policy topics run an LLM formatter on save
   * and must NOT auto-save on every keystroke, so they leave this off.
   */
  autoSave?: boolean;
  /**
   * When true, renders as a section inside a host page (a policy worksheet)
   * instead of a standalone Details page: no outer page container, section-size
   * heading. Loading/saving is unchanged — these are still location vars with
   * their own save path, separate from the host worksheet's answers.
   */
  embedded?: boolean;
  /**
   * The topic renders its own per-section Edit controls, so the page-level Edit
   * button is suppressed. The save bar still works the same way — sections call
   * `setEditing` from the topic context to open it.
   */
  sectionEditing?: boolean;
}

type ErrorMap = Record<string, string>;

/**
 * Mounts a Cortex V3 topic component inside a Details V3 worksheet shell.
 * Loads merged vars for the topic, provides the inheritance/draft context,
 * renders the topic component, and shows a sticky save bar when dirty.
 *
 * Loads and saves a single topic's vars, so each worksheet page is
 * self-contained.
 *
 * See docs/snowy/plans/2026-05-01-details-v3.md.
 */
export function CortexTopicWorksheet({
  topicId,
  heading,
  description,
  autoSave,
  embedded,
  sectionEditing
}: CortexTopicWorksheetProps): React.ReactElement {
  const page = usePropertiesGetLocationIds();
  const orgId = Member.orgId;
  const locationId = page.locationId;
  // A LocationVariable row is org-, group-, or location-scoped — never both
  // group and location. The Details v3 URL is /group/:g/location/:l, so the
  // hook reports both ids at location scope; send only the narrower one.
  const groupId = locationId ? undefined : page.groupId;

  const [merged, setMerged] = useState<Record<string, MergedLocationVar>>({});
  // What a field falls back to once its override here is deleted: whatever the
  // next scope up resolves to. At org scope there is nothing above, so this stays
  // undefined and the fallback is the VarDef default.
  const [parentMerged, setParentMerged] = useState<Record<string, MergedLocationVar> | undefined>(undefined);
  const [draft, setDraft] = useState<TopicDraft>({});
  const [errors, setErrors] = useState<ErrorMap>({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Auto-save topics have no explicit edit step — their fields are always live.
  const [isEditing, setIsEditing] = useState(false);
  const fieldsEditable = autoSave === true || isEditing;
  const { show: savedShow, flash: flashSaved } = useSavedIndicator();

  const loadTopic = useCallback(async () => {
    if (!orgId) return;
    try {
      const resp = await API.Cortex.getTopicVars(orgId, topicId, groupId, locationId);
      const byKey: Record<string, MergedLocationVar> = {};
      for (const v of resp.vars) byKey[v.key] = v;
      setMerged(byKey);
      setDraft({});
      setErrors({});
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, [orgId, groupId, locationId, topicId]);

  useEffect(() => {
    if (!orgId) return;
    if (page.locationId == null && page.groupId == null) {
      setParentMerged(undefined);
      return;
    }
    const parentGroupId = page.locationId != null ? page.groupId : undefined;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await API.Cortex.getTopicVars(orgId, topicId, parentGroupId, undefined);
        if (cancelled) return;
        const byKey: Record<string, MergedLocationVar> = {};
        for (const v of resp.vars) byKey[v.key] = v;
        setParentMerged(byKey);
      } catch {
        // Tooltip detail only — never block the topic on this.
        if (!cancelled) setParentMerged(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, topicId, page.groupId, page.locationId]);

  useEffect(() => {
    loadTopic().catch(() => {
      /* handled in loadTopic */
    });
  }, [loadTopic]);

  const handlePolicySource = useCallback((key: LocationVarKey, next: string) => {
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], source: next } }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleSettingValue = useCallback((key: LocationVarKey, next: string) => {
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], value: next } }));
  }, []);

  const handleCustomize = useCallback((key: LocationVarKey, seed: string, isPolicy: boolean) => {
    setDraft((prev) => ({ ...prev, [key]: isPolicy ? { source: seed } : { value: seed } }));
  }, []);

  const handleDiscard = useCallback((key: LocationVarKey) => {
    setDraft((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleRevert = useCallback(
    async (row: MergedLocationVar) => {
      if (!orgId || !row.id) return;
      try {
        await API.LocationVars.deleteVariable(orgId, row.id);
        // Refresh values but keep the other rows' drafts. `loadTopic` clears the
        // whole draft, which would silently discard unsaved edits elsewhere on
        // the topic just because one field was reverted.
        const resp = await API.Cortex.getTopicVars(orgId, topicId, groupId, locationId);
        const byKey: Record<string, MergedLocationVar> = {};
        for (const v of resp.vars) byKey[v.key] = v;
        setMerged(byKey);
        setDraft((prev) => {
          if (!(row.key in prev)) return prev;
          const { [row.key]: _dropped, ...rest } = prev;
          return rest;
        });
        setErrors((prev) => {
          if (!prev[row.key]) return prev;
          const { [row.key]: _dropped, ...rest } = prev;
          return rest;
        });
        setLoadError(null);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : String(err));
      }
    },
    [orgId, groupId, locationId, topicId]
  );

  /**
   * Drop every staged edit, returning each field to its last saved state.
   * Saved overrides are untouched — those are removed per row via Revert.
   */
  const handleCancelEdits = useCallback((): void => {
    setDraft({});
    setErrors({});
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!orgId || Object.keys(draft).length === 0) return;
    setSaving(true);
    try {
      // Build the legacy section-based payload. Since Details V3 topics are
      // structured-var only (no policies), every draft entry is a setting `value`.
      // Wrap them all under a single synthetic "default" section.
      const settingValues: Record<string, string> = {};
      for (const [key, entry] of Object.entries(draft)) {
        if (entry.value !== undefined) settingValues[key] = entry.value;
      }
      // Cortex.saveTopic's old contract uses { sectionId, settingValues } where
      // `settingValues` keys are setting.key (not varKey). Since we don't have
      // section/setting metadata here (structured-var topics have empty sections),
      // we fall through to per-var direct upsert via saveRaw instead.
      const patches = Object.entries(draft)
        .filter(([, entry]) => entry.value !== undefined)
        .map(([key, entry]) => ({ key: key as LocationVarKey, value: String(entry.value), source: null }));
      if (patches.length > 0) {
        await API.Cortex.saveRaw({ orgId, groupId, locationId, patches });
        // Refresh the global cache so other consumers stop seeing pre-save values.
        await LocationVarsModel.loadAllVariables(true);
      }
      await loadTopic();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [orgId, groupId, locationId, draft, loadTopic]);

  // ── Auto-save (opt-in) ──────────────────────────────────────────────────
  // Persists drafts a short beat after the last edit. The reload keeps any
  // entries the operator changed AFTER the in-flight snapshot, so a save that
  // lands mid-typing never clobbers newer keystrokes.
  const autoSaveCommit = useCallback(async () => {
    if (!orgId) return;
    const snapshot = draft;
    const patches = Object.entries(snapshot)
      .filter(([key, entry]) => entry.value !== undefined && entry.value !== (merged[key]?.value ?? ""))
      .map(([key, entry]) => ({ key: key as LocationVarKey, value: String(entry.value), source: null }));
    if (patches.length === 0) return;
    setSaving(true);
    try {
      await API.Cortex.saveRaw({ orgId, groupId, locationId, patches });
      // Fire-and-forget: this fires per keystroke commit, so it must never add save latency.
      void LocationVarsModel.loadAllVariables(true);
      const resp = await API.Cortex.getTopicVars(orgId, topicId, groupId, locationId);
      const byKey: Record<string, MergedLocationVar> = {};
      for (const v of resp.vars) byKey[v.key] = v;
      setMerged(byKey);
      setDraft((prev) => {
        const kept: TopicDraft = {};
        for (const [key, entry] of Object.entries(prev)) {
          if (snapshot[key]?.value !== entry.value) kept[key] = entry;
        }
        return kept;
      });
      flashSaved();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [orgId, groupId, locationId, topicId, draft, merged, flashSaved]);

  useEffect(() => {
    if (!autoSave || saving || Object.keys(draft).length === 0) return;
    const timer = setTimeout(() => {
      void autoSaveCommit();
    }, 700);
    return () => clearTimeout(timer);
  }, [autoSave, saving, draft, autoSaveCommit]);

  const handleSaveAndClose = useCallback(async (): Promise<void> => {
    await handleSave();
    setIsEditing(false);
  }, [handleSave]);

  const dirtyCount = Object.keys(draft).length;

  if (loadError) {
    return <div className="p-6 text-sm text-red-500">Failed to load: {loadError}</div>;
  }
  if (!orgId) {
    return <div className="p-6 text-sm text-grey-500">No active org.</div>;
  }

  const Component = TOPIC_COMPONENTS[topicId];
  if (!Component) {
    return (
      <div className="p-6 text-sm text-red-500">
        Topic “{topicId}” has no registered page in TOPIC_COMPONENTS. Add it to apps/studio/src/Views/Properties/CortexV3/Topics/index.ts.
      </div>
    );
  }

  return (
    <div className={embedded ? undefined : "mx-auto max-w-[960px] p-6"}>
      {/* Embedded topics render their own card header (see LeadCreation.Topic), so
          nothing is drawn above them here. */}
      {embedded
        ? null
        : (heading || description) && (
            <header className="mb-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                {heading && (
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-1 shrink-0 bg-grey-900" />
                    <h2 className="text-base font-semibold text-grey-900">{heading}</h2>
                  </div>
                )}
                {description && <p className="pl-3 text-base text-grey-600">{description}</p>}
              </div>
            </header>
          )}

      <TopicProvider
        merged={merged}
        parentMerged={parentMerged}
        draft={draft}
        errors={errors}
        isSwivlStaff={Member.isSwivlStaff === true}
        isEditing={fieldsEditable}
        explicitEditing={autoSave !== true}
        setEditing={setIsEditing}
        saving={saving}
        dirtyCount={dirtyCount}
        saveTopic={handleSaveAndClose}
        cancelEdits={handleCancelEdits}
        savedVisible={autoSave ? savedShow : undefined}
        onPolicySource={handlePolicySource}
        onSettingValue={handleSettingValue}
        onCustomize={handleCustomize}
        onDiscard={handleDiscard}
        onRevert={handleRevert}
      >
        <Component />
      </TopicProvider>

      {/* Section-editing topics put Save/Cancel in their own section headers. */}
      {!autoSave && !sectionEditing && dirtyCount > 0 && (
        <div className="sticky bottom-0 -mx-6 mt-6 border-t border-grey-100 bg-white px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-grey-500">{dirtyCount} field(s) with unsaved changes</span>
            <Button variant={ButtonVariant.PRIMARY} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
